/**
 * Single source of truth for all user-tunable settings.
 *
 * Owns one typed `UserSettings` object, persisted as a single localStorage blob
 * (replacing the legacy per-key storage — migrated transparently on first load).
 * The per-feature setup modules (height, hand, locomotion, sword, targets, zones)
 * are thin typed adapters over this store: they keep their own range validation
 * but delegate persistence here. The Firebase layer mirrors this object to
 * Firestore (last-write-wins by `updatedAt`).
 */
import {
  ARCADE_CUT_DIR_TOLERANCE_DEFAULT_DEG,
  ARCADE_CUT_DIR_TOLERANCE_MAX_DEG,
  ARCADE_CUT_DIR_TOLERANCE_MIN_DEG,
  ARCADE_MIN_SPEED_DEFAULT_MPS,
  ARCADE_MIN_SPEED_MAX_MPS,
  ARCADE_MIN_SPEED_MIN_MPS,
  AUDIO_MUSIC_VOLUME_DEFAULT,
  AUDIO_SFX_VOLUME_DEFAULT,
  AUDIO_VOLUME_MAX,
  AUDIO_VOLUME_MIN,
  ARCADE_SIZE_FACTOR_DEFAULT,
  ARCADE_SIZE_FACTOR_MAX,
  ARCADE_SIZE_FACTOR_MIN,
  ARCADE_SPAWN_DELAY_DEFAULT_MS,
  ARCADE_SPAWN_DELAY_MAX_MS,
  ARCADE_SPAWN_DELAY_MIN_MS,
  ARCADE_TTL_DEFAULT_MS,
  ARCADE_TTL_MAX_MS,
  ARCADE_TTL_MIN_MS,
  DOMINANT_HAND,
  ENVIRONMENT_ID,
  GUARD_TUNING_DEFAULT,
  GUARD_TUNING_RANGES,
  LEADERBOARD_NICKNAME_MAX_LEN,
  PELL_STRIKE_MIN_ARC_DEFAULT_M,
  PELL_STRIKE_MIN_ARC_MAX_M,
  PELL_STRIKE_MIN_ARC_MIN_M,
  PELL_STRIKE_MIN_SPEED_DEFAULT_MPS,
  PELL_STRIKE_MIN_SPEED_MAX_MPS,
  PELL_STRIKE_MIN_SPEED_MIN_MPS,
  SETTINGS_ANGLE_MAX_DEG,
  SETTINGS_ANGLE_MIN_DEG,
  SETTINGS_HEIGHT_MAX_CM,
  SETTINGS_HEIGHT_MIN_CM,
  STORAGE_KEYS,
  SWORD_PITCH_DEFAULT_DEG,
  SWORD_ROLL_DEFAULT_DEG,
  TURN_MODE,
  TARGETS_DISTANCE_MAX_M,
  TARGETS_DISTANCE_MIN_M,
  TARGETS_MIN_SPEED_DEFAULT_MPS,
  TARGETS_MIN_SPEED_MAX_MPS,
  TARGETS_MIN_SPEED_MIN_MPS,
  TARGETS_SIZE_FACTOR_DEFAULT,
  TARGETS_SIZE_FACTOR_MAX,
  TARGETS_SIZE_FACTOR_MIN,
  TARGETS_SPAWN_DELAY_DEFAULT_MS,
  TARGETS_SPAWN_DELAY_MAX_MS,
  TARGETS_SPAWN_DELAY_MIN_MS,
  TARGETS_SPAWN_DISTANCE_M,
  TARGETS_TIP_MIN_T_DEFAULT,
  TARGETS_TIP_MIN_T_MAX,
  TARGETS_TIP_MIN_T_MIN,
  TARGETS_TTL_DEFAULT_MS,
  TARGETS_TTL_MAX_MS,
  TARGETS_TTL_MIN_MS,
  USER_HEIGHT_DEFAULT_CM,
  type DominantHand,
  type EnvironmentId,
  type GuardTuning,
  type TurnMode,
} from '../config/constants';
import type { Locale } from '../i18n';

export type UserSettings = {
  userHeightCm: number;
  dominantHand: DominantHand;
  turnMode: TurnMode;
  swordPitchDeg: number;
  swordYawDeg: number;
  swordRollDeg: number;
  targetsSizeFactor: number;
  targetsMinSpeed: number;
  targetsDistance: number;
  targetsTtl: number;
  targetsSpawnDelay: number;
  /** Thrust (custom): tip-gate — fraction of the blade near the punta that counts as a thrust. */
  targetsTipMinT: number;
  /** Arcade ("Sfida"): target lifetime before auto-miss (ms). */
  arcadeTtl: number;
  /** Arcade: delay between a target resolving and the next spawning (ms). */
  arcadeSpawnDelay: number;
  /** Arcade: angular tolerance (deg) on the required cut direction. */
  arcadeCutTolerance: number;
  /** Arcade: disc size multiplier. */
  arcadeSizeFactor: number;
  /** Arcade cut (custom): minimum tip speed (m/s) for a slice to count. */
  arcadeMinSpeed: number;
  /** Pell/drill: minimum impact tip speed (m/s) for a valid colpo. */
  pellMinSpeed: number;
  /** Pell/drill: minimum swing-arc length (m) for a valid colpo. */
  pellMinArc: number;
  /** Four internal zone-band boundaries (fraction of body height), bottom→top. */
  zoneBoundaries: number[];
  /** Guard-recognition tunables (the guard settings tab). */
  guardTuning: GuardTuning;
  /** Training environment (scene HDRI + floor). Selected on the landing page. */
  environment: EnvironmentId;
  locale: Locale;
  /** Shared-leaderboard display name (issue #35). Empty = not yet set (a default is generated lazily). */
  nickname: string;
  /** Master SFX volume (0..1): hits, thrusts, UI clicks. */
  sfxVolume: number;
  /** Background-music volume (0..1). */
  musicVolume: number;
};

export const DEFAULT_SETTINGS: UserSettings = {
  userHeightCm: USER_HEIGHT_DEFAULT_CM,
  dominantHand: DOMINANT_HAND.right,
  turnMode: TURN_MODE.snap30,
  swordPitchDeg: SWORD_PITCH_DEFAULT_DEG,
  swordYawDeg: 0,
  swordRollDeg: SWORD_ROLL_DEFAULT_DEG,
  targetsSizeFactor: TARGETS_SIZE_FACTOR_DEFAULT,
  targetsMinSpeed: TARGETS_MIN_SPEED_DEFAULT_MPS,
  targetsDistance: TARGETS_SPAWN_DISTANCE_M,
  targetsTtl: TARGETS_TTL_DEFAULT_MS,
  targetsSpawnDelay: TARGETS_SPAWN_DELAY_DEFAULT_MS,
  targetsTipMinT: TARGETS_TIP_MIN_T_DEFAULT,
  arcadeTtl: ARCADE_TTL_DEFAULT_MS,
  arcadeSpawnDelay: ARCADE_SPAWN_DELAY_DEFAULT_MS,
  arcadeCutTolerance: ARCADE_CUT_DIR_TOLERANCE_DEFAULT_DEG,
  arcadeSizeFactor: ARCADE_SIZE_FACTOR_DEFAULT,
  arcadeMinSpeed: ARCADE_MIN_SPEED_DEFAULT_MPS,
  pellMinSpeed: PELL_STRIKE_MIN_SPEED_DEFAULT_MPS,
  pellMinArc: PELL_STRIKE_MIN_ARC_DEFAULT_M,
  zoneBoundaries: [],
  guardTuning: { ...GUARD_TUNING_DEFAULT },
  environment: ENVIRONMENT_ID.forest,
  locale: 'it',
  nickname: '',
  sfxVolume: AUDIO_SFX_VOLUME_DEFAULT,
  musicVolume: AUDIO_MUSIC_VOLUME_DEFAULT,
};

const BLOB_KEY = 'userSettings';

type Persisted = { data: UserSettings; updatedAt: number };

type Listener = () => void;

let state: UserSettings = { ...DEFAULT_SETTINGS };
let updatedAt = 0;
let loaded = false;
const listeners = new Set<Listener>();
/** Suppress change notifications while applying remote/cloud state. */
let muted = false;

/** Initialise the store from localStorage (or migrate legacy per-key values). Idempotent. */
export function loadUserSettings(): void {
  if (loaded) return;
  loaded = true;
  const blob = readBlob();
  if (blob) {
    state = blob.data; // already sanitized by readBlob
    updatedAt = blob.updatedAt;
    return;
  }
  state = migrateLegacy();
  updatedAt = 0;
  persist();
}

export function getSetting<K extends keyof UserSettings>(key: K): UserSettings[K] {
  if (!loaded) loadUserSettings();
  return state[key];
}

export function setSetting<K extends keyof UserSettings>(key: K, value: UserSettings[K]): void {
  if (!loaded) loadUserSettings();
  if (state[key] === value) return;
  state = { ...state, [key]: value };
  updatedAt = Date.now();
  persist();
  notify();
}

/**
 * Reset every setting to its default. Bumps `updatedAt` so the cloud copy is
 * overwritten last-write-wins, and notifies listeners (which schedules a push).
 */
export function resetSettings(): void {
  if (!loaded) loadUserSettings();
  state = { ...DEFAULT_SETTINGS };
  updatedAt = Date.now();
  persist();
  notify();
}

export function getAllSettings(): UserSettings {
  if (!loaded) loadUserSettings();
  return { ...state };
}

export function getSettingsUpdatedAt(): number {
  if (!loaded) loadUserSettings();
  return updatedAt;
}

/**
 * Replace the whole settings object from a remote (cloud) source without
 * re-broadcasting a change (avoids a push feedback loop). Used by the Firebase
 * sync layer when the cloud copy wins last-write-wins.
 */
export function applyRemoteSettings(data: unknown, remoteUpdatedAt: number): void {
  muted = true;
  state = sanitizeSettings(data);
  updatedAt = remoteUpdatedAt;
  persist();
  muted = false;
  notify();
}

/**
 * Coerce an untrusted settings blob (cloud pull, localStorage) into a valid
 * `UserSettings`: numerics clamped to the settings-panel ranges, unions checked
 * against the known values, anything else replaced by its default. Pure.
 */
export function sanitizeSettings(raw: unknown): UserSettings {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Partial<
    Record<keyof UserSettings, unknown>
  >;
  const d = DEFAULT_SETTINGS;
  return {
    userHeightCm: clamp(
      r.userHeightCm,
      SETTINGS_HEIGHT_MIN_CM,
      SETTINGS_HEIGHT_MAX_CM,
      d.userHeightCm,
    ),
    dominantHand: oneOf(r.dominantHand, DOMINANT_HAND, d.dominantHand),
    turnMode: oneOf(r.turnMode, TURN_MODE, d.turnMode),
    swordPitchDeg: clamp(
      r.swordPitchDeg,
      SETTINGS_ANGLE_MIN_DEG,
      SETTINGS_ANGLE_MAX_DEG,
      d.swordPitchDeg,
    ),
    swordYawDeg: clamp(
      r.swordYawDeg,
      SETTINGS_ANGLE_MIN_DEG,
      SETTINGS_ANGLE_MAX_DEG,
      d.swordYawDeg,
    ),
    swordRollDeg: clamp(
      r.swordRollDeg,
      SETTINGS_ANGLE_MIN_DEG,
      SETTINGS_ANGLE_MAX_DEG,
      d.swordRollDeg,
    ),
    targetsSizeFactor: clamp(
      r.targetsSizeFactor,
      TARGETS_SIZE_FACTOR_MIN,
      TARGETS_SIZE_FACTOR_MAX,
      d.targetsSizeFactor,
    ),
    targetsMinSpeed: clamp(
      r.targetsMinSpeed,
      TARGETS_MIN_SPEED_MIN_MPS,
      TARGETS_MIN_SPEED_MAX_MPS,
      d.targetsMinSpeed,
    ),
    targetsDistance: clamp(
      r.targetsDistance,
      TARGETS_DISTANCE_MIN_M,
      TARGETS_DISTANCE_MAX_M,
      d.targetsDistance,
    ),
    targetsTtl: clamp(r.targetsTtl, TARGETS_TTL_MIN_MS, TARGETS_TTL_MAX_MS, d.targetsTtl),
    targetsSpawnDelay: clamp(
      r.targetsSpawnDelay,
      TARGETS_SPAWN_DELAY_MIN_MS,
      TARGETS_SPAWN_DELAY_MAX_MS,
      d.targetsSpawnDelay,
    ),
    targetsTipMinT: clamp(
      r.targetsTipMinT,
      TARGETS_TIP_MIN_T_MIN,
      TARGETS_TIP_MIN_T_MAX,
      d.targetsTipMinT,
    ),
    arcadeTtl: clamp(r.arcadeTtl, ARCADE_TTL_MIN_MS, ARCADE_TTL_MAX_MS, d.arcadeTtl),
    arcadeSpawnDelay: clamp(
      r.arcadeSpawnDelay,
      ARCADE_SPAWN_DELAY_MIN_MS,
      ARCADE_SPAWN_DELAY_MAX_MS,
      d.arcadeSpawnDelay,
    ),
    arcadeCutTolerance: clamp(
      r.arcadeCutTolerance,
      ARCADE_CUT_DIR_TOLERANCE_MIN_DEG,
      ARCADE_CUT_DIR_TOLERANCE_MAX_DEG,
      d.arcadeCutTolerance,
    ),
    arcadeSizeFactor: clamp(
      r.arcadeSizeFactor,
      ARCADE_SIZE_FACTOR_MIN,
      ARCADE_SIZE_FACTOR_MAX,
      d.arcadeSizeFactor,
    ),
    arcadeMinSpeed: clamp(
      r.arcadeMinSpeed,
      ARCADE_MIN_SPEED_MIN_MPS,
      ARCADE_MIN_SPEED_MAX_MPS,
      d.arcadeMinSpeed,
    ),
    pellMinSpeed: clamp(
      r.pellMinSpeed,
      PELL_STRIKE_MIN_SPEED_MIN_MPS,
      PELL_STRIKE_MIN_SPEED_MAX_MPS,
      d.pellMinSpeed,
    ),
    pellMinArc: clamp(
      r.pellMinArc,
      PELL_STRIKE_MIN_ARC_MIN_M,
      PELL_STRIKE_MIN_ARC_MAX_M,
      d.pellMinArc,
    ),
    zoneBoundaries: sanitizeZoneBoundaries(r.zoneBoundaries),
    guardTuning: sanitizeGuardTuning(r.guardTuning),
    environment: oneOf(r.environment, ENVIRONMENT_ID, d.environment),
    locale: r.locale === 'it' || r.locale === 'en' ? r.locale : d.locale,
    nickname: sanitizeNickname(r.nickname),
    sfxVolume: clamp(r.sfxVolume, AUDIO_VOLUME_MIN, AUDIO_VOLUME_MAX, d.sfxVolume),
    musicVolume: clamp(r.musicVolume, AUDIO_VOLUME_MIN, AUDIO_VOLUME_MAX, d.musicVolume),
  };
}

/** Trim and clamp a nickname to the leaderboard max length; non-strings collapse to `''`. */
function sanitizeNickname(v: unknown): string {
  return typeof v === 'string' ? v.trim().slice(0, LEADERBOARD_NICKNAME_MAX_LEN) : '';
}

/** Clamp each guard tunable to its slider range; anything off falls back to the default. */
function sanitizeGuardTuning(raw: unknown): GuardTuning {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Partial<
    Record<keyof GuardTuning, unknown>
  >;
  const out = { ...GUARD_TUNING_DEFAULT };
  for (const key of Object.keys(out) as Array<keyof GuardTuning>) {
    const range = GUARD_TUNING_RANGES[key];
    out[key] = clamp(r[key], range.min, range.max, GUARD_TUNING_DEFAULT[key]);
  }
  return out;
}

function clamp(v: unknown, min: number, max: number, def: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? Math.min(max, Math.max(min, v)) : def;
}

function oneOf<T extends string>(v: unknown, allowed: Record<string, T>, def: T): T {
  return Object.values(allowed).includes(v as T) ? (v as T) : def;
}

/**
 * Accept only four finite, strictly ascending fractions inside (0,1) — the
 * shape `zoneBands` expects. Anything else collapses to `[]`, which makes
 * `zoneBands` fall back to its built-in default boundaries.
 */
function sanitizeZoneBoundaries(v: unknown): number[] {
  if (!Array.isArray(v) || v.length !== 4) return [];
  const out: number[] = [];
  for (const item of v) {
    const n = typeof item === 'number' ? item : NaN;
    if (!Number.isFinite(n) || n <= 0 || n >= 1) return [];
    if (out.length > 0 && n <= out[out.length - 1]!) return [];
    out.push(n);
  }
  return out;
}

/** Subscribe to local settings changes. Returns an unsubscribe fn. */
export function subscribeSettings(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify(): void {
  if (muted) return;
  for (const fn of listeners) fn();
}

/** localStorage is absent in some runtimes (e.g. unit tests). Tolerate that. */
function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function persist(): void {
  try {
    const payload: Persisted = { data: state, updatedAt };
    storage()?.setItem(BLOB_KEY, JSON.stringify(payload));
  } catch (err) {
    console.warn('persist userSettings failed', err);
  }
}

function readBlob(): Persisted | null {
  try {
    const raw = storage()?.getItem(BLOB_KEY) ?? null;
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Persisted>;
    if (!parsed || typeof parsed !== 'object' || !parsed.data) return null;
    return {
      data: sanitizeSettings(parsed.data),
      updatedAt: typeof parsed.updatedAt === 'number' ? parsed.updatedAt : 0,
    };
  } catch {
    return null;
  }
}

/** Build the initial settings object from legacy per-key localStorage entries. */
function migrateLegacy(): UserSettings {
  const ls = storage();
  const s: UserSettings = { ...DEFAULT_SETTINGS };
  if (!ls) return s;
  const num = (key: string, def: number): number => {
    const raw = ls.getItem(key);
    const n = Number(raw);
    return raw !== null && Number.isFinite(n) ? n : def;
  };
  s.userHeightCm = num(STORAGE_KEYS.userHeightCm, s.userHeightCm);
  const hand = ls.getItem(STORAGE_KEYS.dominantHand);
  if (hand === DOMINANT_HAND.left || hand === DOMINANT_HAND.right) s.dominantHand = hand;
  const turn = ls.getItem(STORAGE_KEYS.turnMode) as TurnMode | null;
  if (turn && Object.values(TURN_MODE).includes(turn)) s.turnMode = turn;
  s.swordPitchDeg = num(STORAGE_KEYS.swordPitchDeg, s.swordPitchDeg);
  s.swordYawDeg = num(STORAGE_KEYS.swordYawDeg, s.swordYawDeg);
  s.swordRollDeg = num(STORAGE_KEYS.swordRollDeg, s.swordRollDeg);
  s.targetsSizeFactor = num(STORAGE_KEYS.targetsSizeFactor, s.targetsSizeFactor);
  s.targetsMinSpeed = num(STORAGE_KEYS.targetsMinSpeed, s.targetsMinSpeed);
  s.targetsDistance = num(STORAGE_KEYS.targetsDistance, s.targetsDistance);
  s.targetsTtl = num(STORAGE_KEYS.targetsTtl, s.targetsTtl);
  s.targetsSpawnDelay = num(STORAGE_KEYS.targetsSpawnDelay, s.targetsSpawnDelay);
  try {
    const rawBands = ls.getItem(STORAGE_KEYS.pellZoneBands);
    if (rawBands) {
      const arr: unknown = JSON.parse(rawBands);
      if (Array.isArray(arr)) s.zoneBoundaries = arr.map(Number);
    }
  } catch {
    /* keep default */
  }
  const loc = ls.getItem('locale');
  if (loc === 'it' || loc === 'en') s.locale = loc;
  return s;
}
